// src/data/circlePulseQuestions.js
//
// Questions for the Friend Circle "pulse". Everyone in the circle picks one
// fixed option, privately. Nobody sees anyone else's answer until everyone
// asked has picked (or, with at least two answers in, three days have
// passed). Then they all show at once, with names, and the point is to ask
// each other why - in person, not in the app.
//
// First written for PR #86 (commit 073f231cc, August 2026) and carried over
// unchanged in substance.
//
// ── THE DESIGN RULE, and it is not negotiable ────────────────────────────────
//
// A question may only ever ask about YOURSELF. Never about another member,
// never ranking people, never "who in this circle is most likely to...", never
// anything where an answer could be a verdict on someone reading it. The whole
// reason this mechanic is safe is that the worst possible answer is a mildly
// surprising fact about the person who gave it.
//
// Fixed options only. No free text, anywhere, ever - free text is the thing
// that turns a shared activity into a place people get hurt.
//
// Options should be genuinely divergent. If four out of five people always
// pick the same one, the question is doing nothing: nobody has anything to ask
// about.
//
// And keep every option something a person would say out loud to a group of
// friends. An option like "I assume they are sick of me" is a real
// vulnerability, and having it revealed to four friends at once is not a small
// thing - the reveal is simultaneous, named, and permanent to everyone who saw
// it. Divergent, yes. Exposing, no.
//
// ── Keeping the rules in step ───────────────────────────────────────────────
//
// firestore.rules lists every question id and how many options it has
// (pulseOptionCounts), so a question that isn't here can't be asked and an
// answer outside its options can't be saved. Adding, removing or re-counting
// a question means changing that map too, and deploying the rules; the rules
// suite fails until the two agree. Never reorder or reword an option in a
// way that changes its meaning: answers are stored as the option's position.

export const CIRCLE_PULSE_QUESTIONS = [
  {
    id: 'pulse-role',
    question: 'Which of these are you, most of the time, in a group?',
    options: [
      'The one who organises everyone',
      'The one who keeps it funny',
      'The one people tell things to',
      'The one who smooths out arguments',
      'The one who is happy to just be there',
    ],
  },
  {
    id: 'pulse-recharge',
    question: 'After a big day with people, what do you actually want?',
    options: [
      'To be completely alone for a bit',
      'One person, something quiet',
      'To keep going, honestly',
      'To not talk, but not be by myself either',
    ],
  },
  {
    id: 'pulse-conflict',
    question: 'When something is wrong between you and a friend, what do you do first?',
    options: [
      'Say something straight away',
      'Wait and see if it sorts itself out',
      'Go quiet until they notice',
      'Talk to someone else about it first',
    ],
  },
  {
    id: 'pulse-plans',
    question: 'Someone cancels on you an hour before. Your honest first reaction?',
    options: [
      "Relief, if I'm honest",
      "Annoyed, and I'd say so",
      "Annoyed, and I'd say it's fine",
      'A bit thrown, depending on the week',
    ],
  },
  {
    id: 'pulse-new-person',
    question: 'A new person joins something you go to every week. You…',
    options: [
      'Say hello first',
      'Wait for them to say something',
      'Are friendly if you end up next to each other',
      'It depends how the day is going',
    ],
  },
  {
    id: 'pulse-reply',
    question: 'How long can a good friend leave you on read before you notice?',
    options: [
      "An hour, and I've noticed",
      'A day is fine',
      "Days. I wouldn't think about it",
      'It depends entirely who it is',
    ],
  },
  {
    id: 'pulse-hard-time',
    question: "When you're having a rough week, what do you actually want from friends?",
    options: [
      'To be asked straight out',
      'To be distracted and not asked',
      'To be left alone until I come back',
      'For someone to just sit with me',
    ],
  },
  {
    id: 'pulse-group-size',
    question: 'Where do you feel most like yourself?',
    options: [
      'One on one',
      'Three or four people',
      'A proper big group',
      'It depends so much on who',
    ],
  },
  {
    id: 'pulse-apology',
    question: 'What makes an apology actually land for you?',
    options: [
      'They say exactly what they did',
      'They change what they do afterwards',
      'They say it quickly, before it festers',
      'Honestly, just acknowledging it at all',
    ],
  },
  {
    id: 'pulse-loyalty',
    question: 'A friend says something unfair about someone you both know. You…',
    options: [
      'Push back there and then',
      'Change the subject',
      'Agree in the moment, feel off about it later',
      'Ask what they mean and let them hear it',
    ],
  },
];

export function getPulseQuestion(id) {
  return CIRCLE_PULSE_QUESTIONS.find((q) => q.id === id) || null;
}

/**
 * The question to ask next. A circle's first question is picked by the week,
 * so circles don't all start on the same one; after that they go round the
 * list in order, so nobody gets the same question twice in a row.
 */
export function nextPulseQuestionId(currentId, now = Date.now()) {
  const n = CIRCLE_PULSE_QUESTIONS.length;
  const at = CIRCLE_PULSE_QUESTIONS.findIndex((q) => q.id === currentId);
  if (at >= 0) return CIRCLE_PULSE_QUESTIONS[(at + 1) % n].id;
  const week = Math.floor(now / (7 * 24 * 60 * 60 * 1000));
  return CIRCLE_PULSE_QUESTIONS[week % n].id;
}
